import React, { useState, useEffect } from 'react';

export default function PostCard({ 
  post, 
  currentUser, 
  onVote, 
  onSharePost, 
  onDeletePost, 
  showToast,
  isCommentsExpanded,
  onToggleComments,
  postComments = [],
  onAddComment,
  targetPostId,
  targetCommentId,
  replyTarget,
  onSetReplyTarget,
  onEditPost 
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [commentInput, setCommentInput] = useState('');
  
  // Local state for editing
  const [isEditing, setIsEditing] = useState(false);
  const [editPostText, setEditPostText] = useState(post.body);

  const isOwn = currentUser?.userId === post.user_id || currentUser?.id === post.user_id;
  const commentsDisabled = post.allow_comments === false || post.allow_comments === 0 || post.allow_comments === '0';

  useEffect(() => {
    if (!menuOpen) return;
    const closeMenu = () => setMenuOpen(false);
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, [menuOpen]);

  // Target expansion and auto-scroll trigger
  useEffect(() => {
    if (String(post.id) === String(targetPostId) && targetCommentId && !isCommentsExpanded) {
      onToggleComments(post.id);
    }
  }, [post.id, targetPostId, targetCommentId, isCommentsExpanded, onToggleComments]);

  // Auto-scroll to specific comment/reply when expanded
  useEffect(() => {
    if (String(post.id) === String(targetPostId) && isCommentsExpanded && targetCommentId && postComments?.length > 0) {
      setTimeout(() => {
        const commentEl = document.getElementById(`comment-${targetCommentId}`);
        if (commentEl) {
          commentEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          commentEl.style.transition = 'background-color 0.5s';
          commentEl.style.backgroundColor = 'rgba(0, 255, 170, 0.2)'; 
          setTimeout(() => { commentEl.style.backgroundColor = 'transparent'; }, 2500);
        }
      }, 400); 
    }
  }, [post.id, targetPostId, isCommentsExpanded, targetCommentId, postComments]);

  const timeAgo = (dateString) => {
    if (!dateString) return 'Just now';
    const diffHours = Math.abs(new Date() - new Date(dateString)) / 36e5;
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${Math.floor(diffHours)}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (!commentInput.trim()) return;
    // Pass the comment body up along with the reply target's parent_id if replying
    onAddComment(post.id, commentInput, replyTarget ? replyTarget.id : null);
    setCommentInput('');
    if (onSetReplyTarget) onSetReplyTarget(null);
  };

  const handleEditSubmit = () => {
    if (!editPostText.trim()) return showToast('Post cannot be empty.');
    onEditPost(post.id, editPostText);
    setIsEditing(false);
  };

  // --- NESTING HELPER FUNCTIONS ---
  // 1. Extract Top-Level comments (parent_id is null/undefined)
  const rootComments = postComments.filter(c => !c.parent_id);

  // 2. Extract Direct Replies to a specific comment ID
  const getReplies = (parentId) => {
    return postComments.filter(c => String(c.parent_id) === String(parentId));
  };

  // Single Comment Item Component (Handles both Top-Level Comments & Replies)
  const renderCommentItem = (comment, isReply = false) => {
    const isTarget = String(targetCommentId) === String(comment.id);
    const childReplies = getReplies(comment.id);

    return (
      <div key={comment.id} style={{ marginBottom: '8px' }}>
        <div 
          className="comment-item" 
          id={`comment-${comment.id}`} 
          style={{ 
            display: 'flex', 
            gap: '8px', 
            padding: isTarget ? '8px' : '4px', 
            backgroundColor: isTarget ? 'rgba(0, 255, 170, 0.15)' : 'transparent', 
            borderLeft: isTarget ? '3px solid var(--teal)' : isReply ? '2px solid var(--border)' : 'none', 
            borderRadius: '4px', 
            marginLeft: isReply ? '28px' : '0px',
            transition: 'background-color 0.5s ease' 
          }}
        >
          <div className="cm-av" style={{ width: isReply ? '22px' : '26px', height: isReply ? '22px' : '26px', borderRadius: '50%', background: 'var(--teal-l)', color: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 'bold' }}>
            {comment.display_name?.charAt(0) || 'U'}
          </div>
          <div style={{ flex: 1 }}>
            <div className="cm-name" style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{comment.display_name}</span>
              
              <span 
                style={{ color: 'var(--purple)', cursor: 'pointer', fontWeight: 'normal', fontSize: '10px' }} 
                onClick={() => onSetReplyTarget && onSetReplyTarget(comment)}
              >
                Reply
              </span>
            </div>
            <div className="cm-text" style={{ fontSize: '11px', color: 'var(--text2)', marginTop: '2px' }}>{comment.body}</div>
          </div>
        </div>

        {/* NESTED REPLIES (Renders recursivly under parent comment) */}
        {childReplies.length > 0 && (
          <div className="comment-replies-container">
            {childReplies.map(reply => renderCommentItem(reply, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="post-card" id={`post-${post.id}`} style={{ border: '1px solid var(--border)', padding: '16px', borderRadius: '12px', background: 'var(--bg2)', marginBottom: '12px', position: 'relative' }}>
      
      {/* POST HEADER */}
      <div className="post-header" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <div className="post-av" style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'var(--purple-l)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          {post.avatar_url ? (
            <img src={post.avatar_url} alt="av" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            post.display_name?.charAt(0).toUpperCase() || 'U'
          )}
        </div>
        
        <div style={{ flex: 1 }}>
          <div className="post-name" style={{ fontWeight: 'bold', color: 'var(--text)' }}>
            {post.display_name}
          </div>
          <div className="post-time" style={{ fontSize: '12px', color: 'var(--text2)' }}>
            @{post.handle} · {timeAgo(post.created_at)}
          </div>
        </div>

        <button className="post-menu" style={{ background: 'none', border: 'none', color: 'var(--text2)', cursor: 'pointer', fontSize: '18px' }} onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}>
          ···
        </button>

        {menuOpen && (
          <div className="post-context-menu" onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', right: '0', top: '35px', background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: '10px', padding: '6px', minWidth: '180px', zIndex: 50, boxShadow: '0 8px 24px rgba(0,0,0,.4)' }}>
            {isOwn ? (
              <>
                <div className="pcm-item" style={{ padding: '8px', cursor: 'pointer', borderRadius: '6px' }} onClick={() => { setIsEditing(true); setMenuOpen(false); }}>✏️ Edit post</div>
                <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }}></div>
                <div className="pcm-item danger" style={{ padding: '8px', cursor: 'pointer', borderRadius: '6px', color: 'var(--red)' }} onClick={() => { onDeletePost(post.id); setMenuOpen(false); }}>🗑️ Delete post</div>
              </>
            ) : (
              <>
                <div className="pcm-item" style={{ padding: '8px', cursor: 'pointer', borderRadius: '6px' }} onClick={() => { showToast('Reposted!'); setMenuOpen(false); }}>↺ Repost</div>
                <div className="pcm-item" style={{ padding: '8px', cursor: 'pointer', borderRadius: '6px' }} onClick={() => { onSharePost(post); setMenuOpen(false); }}>↗ Share</div>
                <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }}></div>
                <div className="pcm-item danger" style={{ padding: '8px', cursor: 'pointer', borderRadius: '6px', color: 'var(--red)' }} onClick={() => { showToast('User blocked'); setMenuOpen(false); }}>🚫 Block user</div>
                <div className="pcm-item danger" style={{ padding: '8px', cursor: 'pointer', borderRadius: '6px', color: 'var(--red)' }} onClick={() => { showToast('Post reported'); setMenuOpen(false); }}>⚑ Report post</div>
              </>
            )}
          </div>
        )}
      </div>

      {/* POST CONTENT / EDIT MODE */}
      {isEditing ? (
        <div className="edit-post-container" style={{ marginTop: '12px' }}>
          <textarea value={editPostText} onChange={(e) => setEditPostText(e.target.value)} style={{ width: '100%', minHeight: '80px', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: '8px', padding: '10px', color: 'var(--text)', fontFamily: 'inherit', resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px', justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setIsEditing(false)} style={{ padding: '6px 12px', fontSize: '12px' }}>Cancel</button>
            <button className="btn btn-primary" onClick={handleEditSubmit} style={{ padding: '6px 12px', fontSize: '12px' }}>Save</button>
          </div>
        </div>
      ) : (
        <div className="post-body" style={{ whiteSpace: 'pre-wrap', marginTop: '12px', color: 'var(--text)' }}>
          {post.body}
        </div>
      )}
      
      {post.image_url && (
        <div className="post-image" style={{ marginTop: '10px' }}>
          <img src={post.image_url} alt="attachment" style={{ width: '100%', borderRadius: '8px', border: '1px solid var(--border)' }} />
        </div>
      )}

      {/* INTERACTION BAR */}
      <div className="post-actions" style={{ marginTop: '14px', display: 'flex', gap: '16px', alignItems: 'center' }}>
        <button className="post-action" style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => onVote(post.id, 1)}>▲</button>
        <span className="vote-score" style={{ fontSize: '13px', fontWeight: 'bold' }}>{post.vote_score || 0}</span>
        <button className="post-action" style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => onVote(post.id, -1)}>▼</button>

        <button className="post-action" style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }} onClick={() => onToggleComments(post.id)}>
          💬 <span>{post.comment_count || 0}</span>
        </button>
        
        <button className="post-action" style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: 'auto' }} onClick={() => onSharePost(post)}>↗ Share</button>
      </div>
      
      {/* COMMENTS TRAY */}
      {isCommentsExpanded && (
        <div className="comments-section" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
          {!commentsDisabled ? (
            <>
              {/* RENDER TOP LEVEL COMMENTS WITH THEIR NESTED REPLIES */}
              {rootComments.length > 0 ? (
                rootComments.map(comment => renderCommentItem(comment, false))
              ) : (
                <div style={{ fontSize: '11px', color: 'var(--text3)', margin: '8px 0' }}>No comments yet. Be the first to comment!</div>
              )}

              {/* REPLY / COMMENT FORM */}
              <div style={{ width: '100%', marginTop: '12px' }}>
                {replyTarget && (
                  <div style={{ fontSize: '10px', color: 'var(--purple)', marginBottom: '6px', display: 'flex', justifyContent: 'space-between', padding: '0 4px' }}>
                    <span>Replying to @{replyTarget.display_name}</span>
                    <span style={{ cursor: 'pointer', fontWeight: 'bold' }} onClick={() => onSetReplyTarget(null)}>✕ Cancel</span>
                  </div>
                )}
                <form className="comment-input-row" onSubmit={handleCommentSubmit} style={{ display: 'flex', gap: '8px' }}>
                  <input 
                    className="comment-input" 
                    placeholder={replyTarget ? `Replying to ${replyTarget.display_name}...` : "Add a comment..."} 
                    style={{ flex: 1, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: '99px', padding: '6px 12px', fontSize: '12px', color: 'var(--text)' }} 
                    value={commentInput} 
                    onChange={(e) => setCommentInput(e.target.value)} 
                  />
                  <button type="submit" className="cm-send" style={{ background: 'var(--purple)', color: '#fff', border: 'none', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>➤</button>
                </form>
              </div>
            </>
          ) : (
            <div className="comments-disabled-notice" style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: '8px', padding: '10px 14px', textAlign: 'center', fontSize: '12px', color: 'var(--text3)', marginTop: '12px' }}>
              🔒 <span>Comments are disabled for this post.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}